/**
 * Mark passed Important Dates.
 *
 * The shared ImportantDates component renders each date as
 *   <li class="important-date"><time>…</time><span>Title</span></li>
 * in `findResult.ImportantDates` order, but without a machine-readable date in
 * the DOM. This site-scoped helper takes the raw dates, matches them to the
 * rendered items by index, and for any date that has passed strikes through the
 * title and appends a bold red "[PASSED]" — matching the legacy site. Only the
 * title is affected; the date text and surrounding layout are left as-is.
 *
 * @module importantDates
 */

const PASSED_LABEL = "[PASSED]";
const PASSED_MARKER_CLASS = "impt-date-passed";

/**
 * Strike through the titles of passed Important Dates and append "[PASSED]".
 * Idempotent: items already marked are skipped.
 *
 * @param {Array<{Date: string}>} dates - Raw important dates, in render order.
 * @param {Date} [now] - The current time (injectable for testing).
 * @returns {void}
 */
export function markPassedImportantDates(dates, now = new Date()) {
    if (!Array.isArray(dates) || dates.length === 0) {
        return;
    }
    const items = document.querySelectorAll(".important-date");
    items.forEach((item, index) => {
        const raw = dates[index]?.Date;
        if (!raw) {
            return;
        }
        const when = new Date(raw);
        if (Number.isNaN(when.getTime()) || when.getTime() >= now.getTime()) {
            return;
        }
        applyPassed(item);
    });
}

/**
 * Apply the strikethrough + "[PASSED]" marker to a single date item.
 * @param {Element} item - The `.important-date` list item.
 * @returns {void}
 */
function applyPassed(item) {
    if (item.querySelector(`.${PASSED_MARKER_CLASS}`)) {
        return;
    }
    const title = item.querySelector("span");
    if (!title) {
        return;
    }
    title.style.textDecoration = "line-through";

    const passed = document.createElement("strong");
    passed.className = `text-danger ${PASSED_MARKER_CLASS}`;
    passed.textContent = ` ${PASSED_LABEL}`;
    title.after(passed);
}
